import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  GripVertical,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SEASON_PARTS = [
  { name: "Field Season (Autumn)", order: 0 },
  { name: "Indoor Season (Winter)", order: 1 },
  { name: "Field Season (Spring)", order: 2 },
];

export const CreateSeason = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    club_name: "",
    team_name: "",
    title: "",
  });
  const [seasonParts, setSeasonParts] = useState(DEFAULT_SEASON_PARTS);

  // Auto-generate title when team name changes
  const handleTeamNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      team_name: value,
      title: value ? `Season ${value} ${new Date().getFullYear()}-${new Date().getFullYear() + 1}` : ""
    }));
  };

  const handleAddPart = () => {
    setSeasonParts([
      ...seasonParts,
      { name: `New Part ${seasonParts.length + 1}`, order: seasonParts.length }
    ]);
  };

  const handleRemovePart = (index) => {
    if (seasonParts.length <= 1) {
      toast.error("You need at least one season part");
      return;
    }
    const updated = seasonParts.filter((_, i) => i !== index);
    setSeasonParts(updated.map((p, i) => ({ ...p, order: i })));
  };

  const handlePartNameChange = (index, value) => {
    const updated = [...seasonParts];
    updated[index].name = value;
    setSeasonParts(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.club_name || !formData.team_name || !formData.title) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (seasonParts.some(p => !p.name.trim())) {
      toast.error("All season parts must have a name");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/seasons`, {
        ...formData,
        season_parts: seasonParts
      });
      toast.success("Season created successfully!");
      navigate(`/season/${response.data.id}`);
    } catch (error) {
      toast.error("Failed to create season");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="create-season-page" className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/")}
          className="h-10 w-10"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Create Season
          </h1>
          <p className="text-slate-500 mt-1">Set up a new season for your team</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info Card */}
        <Card className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle 
              className="text-lg font-semibold uppercase tracking-tight"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Season Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="club_name" className="text-sm font-medium text-slate-700">
                  Club Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="club_name"
                  placeholder="e.g., HC Naarden"
                  value={formData.club_name}
                  onChange={(e) => setFormData({ ...formData, club_name: e.target.value })}
                  className="h-11"
                  data-testid="club-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="team_name" className="text-sm font-medium text-slate-700">
                  Team Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="team_name"
                  placeholder="e.g., JO14-5"
                  value={formData.team_name}
                  onChange={(e) => handleTeamNameChange(e.target.value)}
                  className="h-11"
                  data-testid="team-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-medium text-slate-700">
                Season Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                placeholder="Season title (auto-generated)"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="h-11"
                data-testid="season-title-input"
              />
              <p className="text-xs text-slate-400">Auto-suggested based on team name</p>
            </div>
          </CardContent>
        </Card>

        {/* Season Parts Card */}
        <Card className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
            <CardTitle 
              className="text-lg font-semibold uppercase tracking-tight"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Season Parts
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddPart}
              className="h-9"
              data-testid="add-part-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Part
            </Button>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              {seasonParts.map((part, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 group fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`season-part-${index}`}
                >
                  <GripVertical className="w-4 h-4 text-slate-300 cursor-grab" />
                  <span className="w-8 h-8 bg-white border border-slate-200 rounded-md flex items-center justify-center text-sm font-medium text-slate-500">
                    {index + 1}
                  </span>
                  <Input
                    value={part.name}
                    onChange={(e) => handlePartNameChange(index, e.target.value)}
                    className="flex-1 h-10 bg-white"
                    placeholder="Part name"
                    data-testid={`part-name-input-${index}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemovePart(index)}
                    className="h-8 w-8 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-part-btn-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Default parts: Field Season (Autumn), Indoor Season (Winter), Field Season (Spring)
            </p>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/")}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 min-w-[140px]"
            data-testid="save-season-btn"
          >
            {saving ? (
              <div className="spinner w-4 h-4 border-white border-t-transparent" />
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Season
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};
